import React, { useCallback } from 'react';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { useGetReadURL } from 'src/components/molecules/UploadedEvidence/hooks/useGetReadURL';
import type { UploadedEvidenceFile } from 'src/components/molecules/UploadedEvidence/types';
import type { DynamicQuestionUploadedFile } from 'src/utils/dynamicQuestionUploadedFiles';
import {
  DownloadFileName,
  ReadOnlyFileButton,
  ReadOnlyFileList,
  ReadOnlyFileListItem,
} from './DynamicQuestion.styles';

function toEvidenceFileShape(
  file: DynamicQuestionUploadedFile,
): UploadedEvidenceFile {
  const ext = file.fileName.includes('.')
    ? (file.fileName.split('.').pop() ?? '')
    : '';
  return {
    dateAdded: new Date(),
    status: null,
    fileExtension: ext,
    fileS3Path: file.s3Path,
  };
}

export interface DynamicQuestionFileDownloadListProps {
  files: DynamicQuestionUploadedFile[];
  reportId: string;
  caseManagerView: boolean;
  'data-testid'?: string;
}

export const DynamicQuestionFileDownloadList: React.FC<
  DynamicQuestionFileDownloadListProps
> = ({ files, reportId, caseManagerView, 'data-testid': dataTestId }) => {
  const { getReadUrl } = useGetReadURL(caseManagerView, reportId);

  const handleOpen = useCallback(
    async (file: DynamicQuestionUploadedFile) => {
      try {
        const url = await getReadUrl(toEvidenceFileShape(file));
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } catch {
        handleErrorToast({
          toastMessage: 'Failed to open file',
        });
      }
    },
    [getReadUrl],
  );

  if (files.length === 0) {
    return null;
  }

  return (
    <ReadOnlyFileList data-testid={dataTestId}>
      {files.map((file, i) => (
        <ReadOnlyFileListItem key={`${file.s3Path}-${i}`}>
          <ReadOnlyFileButton
            type="button"
            onClick={() => void handleOpen(file)}
          >
            <i
              className="uil uil-file-alt"
              aria-hidden
              style={{ fontSize: '1rem' }}
            />
            <DownloadFileName title={file.fileName}>
              {file.fileName}
            </DownloadFileName>
          </ReadOnlyFileButton>
        </ReadOnlyFileListItem>
      ))}
    </ReadOnlyFileList>
  );
};
